import api from '@/services/api';
import { Client } from '@/types/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface UpdateProviderClientDto {
  username?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface CreateClientByProviderDto {
  username: string;
  email?: string;
  phone: string;
  address?: string;
}

// Query keys
export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (search?: string) => [...clientKeys.lists(), search] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientKeys.details(), id] as const,
  search: (query: string) => [...clientKeys.all, 'search', query] as const,
  provider: (search?: string) => [...clientKeys.all, 'provider', search] as const,
};

// Search clients (used in searchable select)
export function useSearchClients(query: string) {
  return useQuery({
    queryKey: clientKeys.search(query),
    queryFn: async () => {
      if (query.length < 2) return [];
      const response = await api.get<Client[]>('/client', {
        params: { search: query },
      });
      return response.data;
    },
    enabled: query.length >= 2,
  });
}

// Single client
export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: clientKeys.detail(id || ''),
    queryFn: async () => {
      if (!id) throw new Error('Client ID is required');
      const response = await api.get<Client>(`/client/${id}`);
      return response.data;
    },
  });
}

// Provider clients
export function useProviderClients(search?: string) {
  return useQuery({
    queryKey: clientKeys.provider(search),
    queryFn: async () => {
      const response = await api.get<Client[]>('/provider/clients', {
        params: { search },
      });
      return response.data;
    },
  });
}

// Create client by provider
export function useCreateProviderClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateClientByProviderDto) => {
      const response = await api.post<{ client: Client }>('/client/register-by-provider', data);
      return response.data.client;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Update provider client (name only before setup)
export function useUpdateProviderClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, data }: { clientId: string; data: UpdateProviderClientDto }) => {
      const response = await api.patch<Client>(`/provider/clients/${clientId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Remove client from provider list (hard delete only if never used)
export function useRemoveProviderClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (clientId: string) => {
      const response = await api.delete<{ deleted: boolean }>(`/provider/clients/${clientId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}
